import styles from './FooterPrimaryNav.scss';

import React, { FC, useRef } from 'react';

import { ExternalLink, LinkSet } from 'src/api/contentful/contentTypes.cloverdotcom';
import { ContentImage, ContentImageEntry } from 'src/components/ContentImage/ContentImage';
import { Grid } from 'src/components/Grid/Grid';
import { ResizeObserverTargetSize, useResizeObserver } from 'src/hooks/UseResizeObserver';
import { useDesktopOrLarge } from 'src/hooks/UseResponsive';
import { FlatEntry } from 'src/services/cms/flattenContentful';
import { componentKeyV2 } from 'src/utils/ComponentKeyV2';

import { FooterLinkSet } from '../FooterLinkSet/FooterLinkSet';
import { FooterSocialLinks } from '../FooterSocialLinks/FooterSocialLinks';

export interface FooterPrimaryNavProps {
  backgroundColor?: string;
  backgroundImage?: ContentImageEntry;
  backgroundSmallImage?: ContentImageEntry;
  items?: Array<FlatEntry<LinkSet>>;
  socialLinks?: Array<FlatEntry<ExternalLink>>;
  onItemsResizeUpdate?: (size: ResizeObserverTargetSize) => void;
}

export const FooterPrimaryNav: FC<FooterPrimaryNavProps> = ({
  items = [],
  socialLinks = [],
  backgroundColor,
  backgroundImage,
  backgroundSmallImage,
  onItemsResizeUpdate,
}) => {
  const itemsRef = useRef<HTMLDivElement>(null);
  const isDesktopOrLarge = useDesktopOrLarge();
  const image = isDesktopOrLarge ? backgroundImage : backgroundSmallImage;
  useResizeObserver({ targetRef: itemsRef, callback: onItemsResizeUpdate });

  return (
    <Grid item xs={12} lg={3} className={styles.footerPrimaryNav}>
      <div className={styles.bg} style={{ backgroundColor }}>
        {image && <ContentImage entry={image} className={styles.bgImage} style={{ width: image.file?.details?.image?.width }} />}
      </div>
      <Grid container justifyContent="center" className={styles.container}>
        <Grid item xs={12} md={9} lg={10} xl={9} container gutter ref={itemsRef}>
          {items.map((entry, index) => (
            <Grid key={componentKeyV2(entry, index)} item xs={12}>
              <FooterLinkSet
                entry={entry}
                className={styles.linkSet}
                listClassName={styles.list}
                listItemClassName={styles.listItem}
                titleClassName={styles.title}
              />
            </Grid>
          ))}
        </Grid>
        {isDesktopOrLarge && (
          <Grid item xs>
            <FooterSocialLinks items={socialLinks} className={styles.socialLinks} />
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

FooterPrimaryNav.displayName = 'FooterPrimaryNav';
